'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, Signpost } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FormInputField } from '@/components/forms/form-input-field';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { ItemsEditor, type ProjectItem } from '@/components/projects/ItemsEditor';
import { ContactsEditor, type ProjectContactDraft } from '@/components/projects/ContactsEditor';
import { ProjectBannerField } from '@/components/projects/ProjectBannerField';
import {
  ProjectDocumentsEditor,
  type ProjectDocumentDraft,
} from '@/components/projects/ProjectDocumentsEditor';
import { RotaryFocusAreasMultiSelect } from '@/components/projects/RotaryFocusAreasMultiSelect';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import axiosInstance from '@/lib/axios';
import { useMyCommunityAvenues } from '@/hooks/use-community-avenues';
import { useCommunity } from '@/hooks/use-community';
import { communityHasAvenues } from '@/lib/community-platform-tags';
import { formatIntegerInput } from '@/lib/integer-input';
import { Input } from '@/components/ui/input';
import {
  buildProjectContactsPayload,
  buildProjectCorePayload,
  buildProjectDocumentsPayload,
  buildProjectItemsPayload,
  validateIncompleteContacts,
} from '@/lib/project-form-payload';

const projectFormSchema = z.object({
  name: z.string().min(1, 'Project name is required').max(200),
  description: z.string().optional(),
  location: z.string().optional(),
  happeningFor: z.string().optional(),
  hostClub: z.string().max(200).optional(),
  needAssessment: z.string().optional(),
  estimatedBeneficiaries: z.string().optional(),
  expectedCost: z.string().optional(),
});

type ProjectFormValues = z.infer<typeof projectFormSchema>;

export default function CreateProjectPage() {
  const { communityId } = useParams<{ communityId: string }>();
  const navigate = useNavigate();
  const { community: communityPayload } = useCommunity(communityId);
  const isAvenuesEnabled = communityHasAvenues(communityPayload?.community?.platformTags);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [items, setItems] = useState<ProjectItem[]>([]);
  const [contacts, setContacts] = useState<ProjectContactDraft[]>([]);
  const [rotaryFocusAreaKeys, setRotaryFocusAreaKeys] = useState<string[]>([]);
  const [bannerImageUrl, setBannerImageUrl] = useState('');
  const [documents, setDocuments] = useState<ProjectDocumentDraft[]>([]);
  const [selectedAvenueId, setSelectedAvenueId] = useState('');
  const { avenues: myAvenues, isLoading: isLoadingAvenues } = useMyCommunityAvenues(
    isAvenuesEnabled ? communityId : undefined
  );
  const isDistrictCreator = isAvenuesEnabled && myAvenues.length > 0;

  const form = useForm<ProjectFormValues>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: {
      name: '',
      description: '',
      location: '',
      happeningFor: '',
      hostClub: '',
      needAssessment: '',
      estimatedBeneficiaries: '',
      expectedCost: '',
    },
  });

  const onSubmit = async (values: ProjectFormValues) => {
    if (!communityId) return;

    const contactError = validateIncompleteContacts(contacts);
    if (contactError) {
      toast.error(contactError);
      return;
    }

    if (isDistrictCreator) {
      const avenueId = myAvenues.length === 1 ? myAvenues[0].id : selectedAvenueId || undefined;
      if (myAvenues.length > 1 && !avenueId) {
        toast.error('Select an avenue for this district project');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const avenueId =
        isDistrictCreator && myAvenues.length === 1
          ? myAvenues[0].id
          : isDistrictCreator
            ? selectedAvenueId
            : undefined;

      const res = await axiosInstance.post(`/api/communities/${communityId}/projects`, {
        ...buildProjectCorePayload(values, {
          bannerImageUrl,
          rotaryFocusAreaKeys,
          alwaysIncludeHostClub: isDistrictCreator,
        }),
        ...(avenueId ? { avenueId } : {}),
        items: buildProjectItemsPayload(items),
        contacts: buildProjectContactsPayload(contacts),
        documents: buildProjectDocumentsPayload(documents),
      });
      const projectId = res.data.id;

      toast.success('Project created successfully!');
      navigate(`/${communityId}/projects/${projectId}`);
    } catch (error) {
      console.error('Failed to create project', error);
      const message =
        axios.isAxiosError(error) &&
        error.response?.data &&
        typeof error.response.data === 'object' &&
        'error' in error.response.data
          ? String((error.response.data as { error: string }).error)
          : 'Failed to create project. Please try again.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/${communityId}/projects`);
  };

  return (
    <Card className="w-full max-w-5xl mx-auto border-0 md:border md:border-border bg-background/50 backdrop-blur-md shadow-none md:shadow-2xl animate-in slide-in-from-bottom-5 duration-300">
      <CardHeader className="px-3 md:px-6">
        <CardTitle className="text-2xl font-black tracking-tight text-foreground">
          Create New Project
        </CardTitle>
        <CardDescription className="text-muted-foreground">
          {isDistrictCreator
            ? 'You are creating a district project for your assigned avenue.'
            : 'Define a new community project or initiative.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="px-3 md:px-6">
        {isLoadingAvenues ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {isDistrictCreator && (
                <div className="rounded-xl border border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10 p-4 space-y-3">
                  <div className="flex items-center gap-2 text-primary font-semibold">
                    <Signpost className="h-4 w-4" />
                    District Project
                  </div>
                  {myAvenues.length === 1 ? (
                    <p className="text-sm text-muted-foreground">
                      This project will be listed under the{' '}
                      <span className="font-medium text-foreground">{myAvenues[0].name}</span>{' '}
                      avenue.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        You belong to multiple avenues. Choose which avenue this project belongs to.
                      </p>
                      <Select value={selectedAvenueId} onValueChange={setSelectedAvenueId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select avenue" />
                        </SelectTrigger>
                        <SelectContent>
                          {myAvenues.map((avenue) => (
                            <SelectItem key={avenue.id} value={avenue.id}>
                              {avenue.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <FormInputField
                    control={form.control}
                    name="hostClub"
                    label="Host Club (optional)"
                    placeholder="e.g., Rotary Club of Downtown"
                  />
                </div>
              )}

              <FormInputField
                control={form.control}
                name="name"
                label="Project Name"
                placeholder="e.g., Community Garden Initiative"
                required
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <RichTextEditor value={field.value || ''} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormInputField
                control={form.control}
                name="location"
                label="Location"
                placeholder="e.g., Community Center Park"
              />

              <FormInputField
                control={form.control}
                name="happeningFor"
                label="Happening For"
                placeholder="e.g., Youth development, Senior citizens"
              />

              <FormField
                control={form.control}
                name="needAssessment"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Need Assessment</FormLabel>
                    <FormControl>
                      <RichTextEditor value={field.value || ''} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInputField
                  control={form.control}
                  name="estimatedBeneficiaries"
                  label="Estimated Beneficiaries"
                  placeholder="e.g., 100"
                  type="number"
                />

                <FormField
                  control={form.control}
                  name="expectedCost"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Expected Cost (₹)</FormLabel>
                      <FormControl>
                        <Input
                          inputMode="numeric"
                          placeholder="e.g., 50,000"
                          value={field.value || ''}
                          onChange={(event) =>
                            field.onChange(formatIntegerInput(event.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="border-t border-border pt-6 space-y-8">
                <RotaryFocusAreasMultiSelect
                  selectedIds={rotaryFocusAreaKeys}
                  onChange={setRotaryFocusAreaKeys}
                  disabled={isSubmitting}
                />
                <ProjectBannerField
                  bannerUrl={bannerImageUrl}
                  onChange={setBannerImageUrl}
                  disabled={isSubmitting}
                  uploadFolder={`projects/${communityId ?? 'unknown'}/banners`}
                />
                <ProjectDocumentsEditor
                  documents={documents}
                  onChange={setDocuments}
                  disabled={isSubmitting}
                  uploadFolder={`projects/${communityId ?? 'unknown'}/documents`}
                />
                <ContactsEditor
                  contacts={contacts}
                  onChange={setContacts}
                  disabled={isSubmitting}
                />
                <ItemsEditor items={items} onChange={setItems} disabled={isSubmitting} />
              </div>

              <div className="flex flex-col-reverse md:flex-row justify-end gap-3 pt-6 border-t border-border">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className="w-full md:w-auto text-muted-foreground hover:text-foreground hover:bg-muted"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full md:w-auto bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 px-8"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Project'
                  )}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </CardContent>
    </Card>
  );
}
